import React, { useMemo, useEffect, useRef } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { LocationFilterHeaderContainer, LocationInputContainer, SearchWrapper, StyledSelect, ClearAllLocationsButton, LocateMeButton, ConsentRequestWrapperMobile, } from '../styles/LocationFilter.styles';
import { useServicesContext } from '../../../context';
import { useDesktopOrMobileView } from '../../../hooks';
import { useFilterData, useClearAllDealerFilters } from '../FilterContextHooks';
import CookieConsentRequest from './CookieConsentRequest';
import { FilterContext } from '../FilterContext';
import { setLocationFilterToDefaultState, syncAutocompleteElementValue, } from '../utils/locationFilterHelpers';
import styled from 'styled-components';
import { Headline, SearchField, } from '@oneaudi/unified-web-components';
import { SYS_SPACE_FIXED_450 } from '@oneaudi/unified-web-common';
import { DynamicComponent, dynamicStylingHelper } from '../../utils';
import { useMandatoryAreaSearchContext } from '../hooks/MandatoryAreaSearchContext';
import { useGoogleMaps } from '../hooks/loadGoogleLibraries';
export const LocationFilterHeader = ({ inputRef, autocompleteRef, locationInputValue, mapConsentButtonText, overlayRef, openConsentOverlay, setIsTwoClickOverlayOpened, setLocationInputValue, handleLocateMeClick, }) => {
    const { effectiveConsent } = useGoogleMaps();
    const { getConfiguration, getEnvironmentConfig } = useServicesContext();
    const context = React.useContext(FilterContext);
    const { radius, setRadius, searchedCoords } = context;
    const audiContentService = useServicesContext().getAdditionalService('audi-content-service');
    let { mileageUnit } = audiContentService.getContent().fields;
    const { locationFilterConfig_radiusOptions } = audiContentService.getContent()
        .fields;
    const radiusOptions = locationFilterConfig_radiusOptions || [10, 20, 50, 100, 200];
    const clearAllDealerFilters = useClearAllDealerFilters();
    const filterData = useFilterData();
    const isDesktop = useDesktopOrMobileView('1024px') === 'desktop';
    const isMobile = useDesktopOrMobileView('767px') === 'mobile';
    const isSomeDealerActive = useMemo(() => {
        var _a, _b;
        const dealersFilterData = (_b = (_a = filterData === null || filterData === void 0 ? void 0 : filterData.groups) === null || _a === void 0 ? void 0 : _a.dealers) === null || _b === void 0 ? void 0 : _b.members;
        return Object.values(dealersFilterData).some((obj) => {
            return obj.active === true;
        });
    }, [filterData === null || filterData === void 0 ? void 0 : filterData.groups.dealers.members]);
    const { enableMandatoryAreaSearch, requireMandatoryAreaSearchLocation } = useMandatoryAreaSearchContext();
    const clearSelectedLocation = () => {
        setLocationFilterToDefaultState(getConfiguration(), getEnvironmentConfig(), clearAllDealerFilters, context, inputRef, enableMandatoryAreaSearch, requireMandatoryAreaSearchLocation);
        syncAutocompleteElementValue(autocompleteRef === null || autocompleteRef === void 0 ? void 0 : autocompleteRef.current, '');
        setLocationInputValue('');
    };
    const suppressBlurRef = useRef(false);
    useEffect(() => {
        if (inputRef) {
            const onScroll = () => {
                if (suppressBlurRef.current)
                    return;
                if ((inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === document.activeElement) {
                    inputRef === null || inputRef === void 0 ? void 0 : inputRef.current.blur();
                }
            };
            if (typeof window !== 'undefined') {
                overlayRef.current.removeEventListener('scroll', onScroll);
                overlayRef.current.addEventListener('scroll', onScroll, { passive: true });
            }
        }
    }, [inputRef]);
    const locationSearchHeadlineLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.locationSearchHeadlineLabel`,
        defaultMessage: 'Location',
    });
    const locationSearchInputLabel = useI18n({
        id: `nemo.sc.plp.locationFilter.locationSearchDefaultInputLabel`,
        defaultMessage: '',
    });
    const clearLocationSearchInputButtonAriaLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.clearLocationSearchInputButtonAriaLabel`,
        defaultMessage: 'Clear the text in the location search input field.',
    });
    const locateMeButtonAriaLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.cookieConsentRequestMap.locateMeButtonAriaLabel`,
        defaultMessage: 'Use your location.',
    });
    const distanceRadiusLabel = useI18n({
        id: `nemo.sc.filter.locationfilter.distanceRadiusLabel`,
        defaultMessage: 'Radius',
    });
    const clearAllLocationsButtonText = useI18n({
        id: `nemo.sc.filter.locationfilter.clearAllLocationsButtonText`,
        defaultMessage: 'Reset location filter',
    });
    const locationIsSearchedOrDealerIsActive = searchedCoords !== undefined || isSomeDealerActive;
    useEffect(() => {
        if (!isMobile || !(inputRef === null || inputRef === void 0 ? void 0 : inputRef.current))
            return undefined;
        const input = inputRef.current;
        const onFocus = () => {
            suppressBlurRef.current = true;
            window.setTimeout(() => {
                suppressBlurRef.current = false;
            }, 400);
            window.setTimeout(() => {
                if (!(overlayRef === null || overlayRef === void 0 ? void 0 : overlayRef.current) || !input)
                    return;
                const overlayRect = overlayRef.current.getBoundingClientRect();
                const inputRect = input.getBoundingClientRect();
                const inputCenterRelativeToOverlay = inputRect.top - overlayRect.top + inputRect.height / 2;
                const targetScrollTop = overlayRef.current.scrollTop +
                    inputCenterRelativeToOverlay -
                    overlayRef.current.clientHeight / 2;
                overlayRef.current.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
            }, 300);
        };
        input.addEventListener('focus', onFocus);
        return () => {
            input.removeEventListener('focus', onFocus);
        };
    }, [inputRef === null || inputRef === void 0 ? void 0 : inputRef.current, isMobile]);
    return (React.createElement(LocationFilterHeaderContainer, null,
        isMobile && (React.createElement(StyledHeadline, { tag: "h4", variant: {
                order: '3',
                type: 'headline',
                weight: 'normal',
            } }, locationSearchHeadlineLabel)),
        !effectiveConsent && !isDesktop && (React.createElement(ConsentRequestWrapperMobile, null,
            React.createElement(CookieConsentRequest, { mapConsentButtonText: mapConsentButtonText, openConsentOverlay: openConsentOverlay, setIsTwoClickOverlayOpened: setIsTwoClickOverlayOpened }))),
        React.createElement(LocationInputContainer, null,
            React.createElement(SearchWrapper, null,
                React.createElement(StyledSearchField, { inputRef: inputRef, additionalIconButton: {
                        onClick: () => {
                            if (dynamicStylingHelper(true, false) && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current)) {
                                clearSelectedLocation();
                            }
                            else {
                                handleLocateMeClick();
                            }
                        },
                        icon: dynamicStylingHelper('cancel', 'gps'),
                        'aria-label': dynamicStylingHelper(clearLocationSearchInputButtonAriaLabel, locateMeButtonAriaLabel),
                    }, disabled: !effectiveConsent, value: locationInputValue, onInputChange: (newValue) => {
                        setLocationInputValue(newValue);
                    }, onChange: (selectedOption) => {
                        if (selectedOption) {
                            setLocationInputValue(selectedOption.label);
                        }
                    }, id: "locationSearchInput", label: locationSearchInputLabel, clearButton: {
                        'aria-label': clearLocationSearchInputButtonAriaLabel,
                        onClick: clearSelectedLocation,
                    } }),
                React.createElement(DynamicComponent, { legacy: React.createElement(LocateMeButton, { variant: "tertiary", icon: "gps", size: "sm", disabled: !effectiveConsent, onClick: () => handleLocateMeClick(), "aria-label": locateMeButtonAriaLabel }) })),
            React.createElement(StyledSelect, { id: "select_radius", label: distanceRadiusLabel, onChange: (e) => {
                    setRadius(e.target.value);
                }, value: radius, disabled: !effectiveConsent || !radius }, radiusOptions.map((radiusOption, index) => {
                return (React.createElement("option", { value: radiusOption, key: index },
                    "+ ",
                    radiusOption,
                    " ",
                    mileageUnit || 'km'));
            })),
            !enableMandatoryAreaSearch && (React.createElement(ClearAllLocationsButton, { variant: "tertiary", iconPosition: "leading", icon: dynamicStylingHelper('replay', 'erase'), size: "md", disabled: !locationIsSearchedOrDealerIsActive, onClick: clearSelectedLocation }, clearAllLocationsButtonText)))));
};
export default LocationFilterHeader;
const StyledHeadline = styled((props) => React.createElement(Headline, Object.assign({}, props))) `
  margin-bottom: ${SYS_SPACE_FIXED_450};
`;
const StyledSearchField = styled((props) => React.createElement(SearchField, Object.assign({}, props))) ``;
